import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, X, Save, Printer, ArrowLeft, Search, FileText, MapPin, Eye, AlertTriangle, CheckCircle, Edit, Trash2, RotateCcw } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import {
  buildSaveOrderPayload,
  calculateOrderPaymentTotals,
  isSaveOrderSuccess,
  normalizeOrderFromApi,
} from '../../utils/orderSave';
import { parseSignedNumberInput, toFiniteNumber } from '../../utils/numbers';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';
import { CONVERT_SHIPMENT_COLUMNS, getConvertShipmentCellValue } from '../ui/tableColumnConfigs';
import { GridText, StatusPill, SELECTED_ROW_CLASS } from '../ui/gridCell';

const parseApiList = (response) => {
  if (Array.isArray(response)) return response;
  if (response?.Data && Array.isArray(response.Data)) return response.Data;
  if (response?.data && Array.isArray(response.data)) return response.data;
  return [];
};

const unwrapApiObject = (response) => response?.Data ?? response?.data ?? response;

const getApiStatus = (response) => {
  if (typeof response === 'string') return response.trim();
  const data = response?.Data ?? response?.data;
  if (typeof data === 'string') return data.trim();
  return String(response?.Status ?? response?.status ?? data?.Status ?? data?.status ?? '').trim();
};

const isApiErrorResponse = (response) => {
  if (!response || typeof response !== 'object') return false;
  return Boolean(
    response.ExceptionMessage ||
    response.exceptionMessage ||
    response.ExceptionType ||
    response.InnerException ||
    response.innerException
  );
};

const getApiErrorMessage = (response) => {
  if (!response) return 'Request failed';
  if (typeof response === 'string') return response.trim() || 'Request failed';
  const inner =
    response.InnerException?.ExceptionMessage ||
    response.innerException?.exceptionMessage ||
    response.InnerException?.Message;
  if (inner) return inner;
  return (
    response.ExceptionMessage ||
    response.exceptionMessage ||
    response.Message ||
    response.message ||
    response.Status ||
    response.status ||
    'Request failed'
  );
};

const isExplicitApiFailure = (response) => {
  if (isApiErrorResponse(response)) return true;
  const status = getApiStatus(response).toLowerCase();
  return response === false || response === 'false' || status === 'failed' || status === 'fail' || status === 'false';
};

const isApiSuccess = (response) => {
  if (isApiErrorResponse(response) || isExplicitApiFailure(response)) return false;
  const status = getApiStatus(response).toLowerCase();
  return response === true || response === 'true' || status === 'success';
};

const toDateInputValue = (value) => {
  if (!value) return new Date().toISOString().split('T')[0];
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString().split('T')[0] : date.toISOString().split('T')[0];
};

const ConvertShipment = () => {
  const { deliveryNoteId, shipmentId } = useParams();
  const isEditMode = Boolean(shipmentId);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deliveryNote, setDeliveryNote] = useState(null);
  const [allDeliveryNoteDetails, setAllDeliveryNoteDetails] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, order: null });
  const [showDriverRemarksModal, setShowDriverRemarksModal] = useState(false);
  const [showChangeAddressModal, setShowChangeAddressModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showEditPaymentsModal, setShowEditPaymentsModal] = useState(false);
  const [showSaveShipmentModal, setShowSaveShipmentModal] = useState(false);
  const [showExpenseBillModal, setShowExpenseBillModal] = useState(false);
  const [showDeleteExpenseDialog, setShowDeleteExpenseDialog] = useState(false);
  const [showUnapproveConfirm, setShowUnapproveConfirm] = useState(false);
  const [showDeleteShipmentConfirm, setShowDeleteShipmentConfirm] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [driverExpenses, setDriverExpenses] = useState([]);
  const [linkedShipmentExpenses, setLinkedShipmentExpenses] = useState([]);
  const [currentExpense, setCurrentExpense] = useState({ ExpenseId: '', Amount: 0, BillNo: '' });
  const [nextShipmentNo, setNextShipmentNo] = useState('');
  const [editPaymentOrder, setEditPaymentOrder] = useState(null);
  const [editPaymentsLoading, setEditPaymentsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [emirates, setEmirates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const locationDropdownRef = useRef(null);
  const [reasons, setReasons] = useState([]);
  const [driverRemarksData, setDriverRemarksData] = useState({ DriverRemarks: '', ReasonId: '', Remarks: '' });
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [statusInDropDown, setStatusInDropDown] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState('');
  const [orderStatusSummary, setOrderStatusSummary] = useState([]);
  const [shipment, setShipment] = useState({
    Amount: 0,
    Date: new Date().toISOString().split('T')[0],
    ExpectedAmount: 0,
    TotalExpense: 0,
    CashInHand: 0,
    TotalIncome: 0,
    TotalVat: 0,
    TotalAdvance: 0,
    BalanceIncome: 0,
    TotalMoneyFromClient: 0,
    TotalClientMoney: 0,
    Remarks: '',
    DeliveryNoteId: deliveryNoteId || '',
  });
  const isShipmentApproved = Boolean(shipment?.IsApproved);

  // Load delivery note details
  const loadDeliveryNoteDetails = async (noteId = deliveryNoteId || shipment.DeliveryNoteId || deliveryNote?.Id) => {
    if (!noteId) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Load delivery note info
      const noteResponse = await MainService.GetDeliveryNoteById(noteId);
      if (noteResponse) {
        console.log('Delivery Note Response:', noteResponse);
        console.log('Vehicle Data:', noteResponse.Vehicle);
        setDeliveryNote(noteResponse);
        setShipment(prev => ({
          ...prev,
          Date: noteResponse.Date ? new Date(noteResponse.Date).toISOString().split('T')[0] : prev.Date
        }));
        
        // Load driver expenses if driver exists
        if (noteResponse.DriverId) {
          await loadDriverExpenses(noteResponse.DriverId);
        }
      }
      
      // Load delivery note details (orders)
      const detailsResponse = await MainService.GetDeliveryDetailsListById(noteId);
      const details = parseApiList(detailsResponse);
      setAllDeliveryNoteDetails(details);
    } catch (err) {
      console.error('Error loading delivery note details:', err);
      setError('Failed to load delivery note details');
    } finally {
      setLoading(false);
    }
  };

  const resolvePositiveId = (...candidates) => {
    for (const value of candidates) {
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  const normalizeShipmentExpenseLink = (row) => ({
    Amount: Number(row?.Amount ?? row?.amount ?? 0) || 0,
    DriverExpenseId: resolvePositiveId(
      row?.DriverExpenseId,
      row?.driverExpenseId,
      row?.DriverExpense?.Id,
      row?.DriverExpense?.id,
      row?.Id,
      row?.id
    ),
    ExpenseId: resolvePositiveId(
      row?.ExpenseId,
      row?.expenseId,
      row?.Expense?.Id,
      row?.Expense?.id
    ),
  });

  const sanitizeShipmentExpensesPayload = (items) =>
    items
      .map(normalizeShipmentExpenseLink)
      .filter((e) => e.DriverExpenseId > 0 && e.ExpenseId > 0);

  const mergeSelectedShipmentExpenses = (baseExpenses, selectedShipmentExpenses) => {
    const selectedRows = sanitizeShipmentExpensesPayload(parseApiList(selectedShipmentExpenses));
    const selectedDriverExpenseIds = new Set(selectedRows.map((x) => x.DriverExpenseId));
    const selectedExpenseIds = new Set(selectedRows.map((x) => x.ExpenseId));

    const rows = baseExpenses.map((expense) => ({
      ...expense,
      IsSelected:
        selectedDriverExpenseIds.has(expense.Id) ||
        selectedDriverExpenseIds.has(expense.DriverExpenseId) ||
        selectedExpenseIds.has(expense.ExpenseId),
    }));

    selectedRows.forEach((shipmentExpense) => {
      const driverExpenseId = shipmentExpense.DriverExpenseId;
      if (!driverExpenseId) return;
      if (rows.some((expense) => expense.Id === driverExpenseId || expense.DriverExpenseId === driverExpenseId)) {
        return;
      }
      rows.push({
        Id: driverExpenseId,
        Amount: shipmentExpense.Amount,
        ExpenseId: shipmentExpense.ExpenseId,
        Expense: { Id: shipmentExpense.ExpenseId, Name: '' },
        IsSelected: true,
      });
    });

    return rows;
  };

  const buildShipmentExpensesPayload = () => {
    const fromDriverSelection = driverExpenses
      .filter((e) => e.IsSelected && resolvePositiveId(e.Id) && resolvePositiveId(e.ExpenseId))
      .map((e) => ({
        Amount: Number(e.Amount) || 0,
        DriverExpenseId: Number(e.Id),
        ExpenseId: Number(e.ExpenseId),
      }));

    if (fromDriverSelection.length > 0) {
      return sanitizeShipmentExpensesPayload(fromDriverSelection);
    }

    return sanitizeShipmentExpensesPayload(linkedShipmentExpenses);
  };

  const loadShipmentForEdit = async () => {
    if (!shipmentId) return;

    try {
      setLoading(true);
      setError('');
      const response = await MainService.ShipmentById(shipmentId);
      const loadedShipment = unwrapApiObject(response);

      if (!loadedShipment) {
        setError('Failed to load shipment');
        return;
      }

      const note = loadedShipment.DeliveryNote || null;
      const noteId = loadedShipment.DeliveryNoteId ?? note?.Id;

      setDeliveryNote(note);
      setShipment((prev) => ({
        ...prev,
        ...loadedShipment,
        Date: toDateInputValue(loadedShipment.Date),
        DeliveryNoteId: noteId || '',
      }));
      setNextShipmentNo(loadedShipment.RefNo || '');

      if (noteId) {
        const detailsResponse = await MainService.GetDeliveryDetailsListById(noteId);
        const details = parseApiList(detailsResponse);
        setAllDeliveryNoteDetails(details);
      }

      if (note?.DriverId) {
        let selectedExpenses = loadedShipment.ShipmentExpenses || [];
        try {
          const shipmentExpensesResponse = await MainService.getShipmentExpenses(loadedShipment.Id);
          const shipmentExpenses = parseApiList(shipmentExpensesResponse);
          if (shipmentExpenses.length > 0) {
            selectedExpenses = shipmentExpenses;
          }
        } catch (expenseErr) {
          console.error('Error loading shipment expenses:', expenseErr);
        }
        const expenseLinks = sanitizeShipmentExpensesPayload(parseApiList(selectedExpenses));
        setLinkedShipmentExpenses(expenseLinks);
        await loadDriverExpenses(note.DriverId, selectedExpenses);
      }
    } catch (err) {
      console.error('Error loading shipment:', err);
      setError('Failed to load shipment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      loadShipmentForEdit();
      return;
    }

    if (deliveryNoteId) {
      loadDeliveryNoteDetails(deliveryNoteId);
      loadNextShipmentNo();
    }
  }, [deliveryNoteId, shipmentId]);

  const deliveryNoteDetails = allDeliveryNoteDetails;

  const selectVoucherByScan = useCallback((raw) => {
    if (isShipmentApproved) return false;
    const keyword = raw.trim();
    if (!keyword) return false;

    const voucherKey = keyword.toLowerCase();
    const match = allDeliveryNoteDetails.find(
      (detail) => (detail.Order?.VoucherNo || '').toLowerCase() === voucherKey
    );

    if (!match) return false;

    setSelectedOrders((prev) => {
      if (prev.has(match.Id)) return prev;
      const next = new Set(prev);
      next.add(match.Id);
      return next;
    });
    setSearchKeyword('');
    setError('');
    return true;
  }, [allDeliveryNoteDetails, isShipmentApproved]);

  useEffect(() => {
    if (isShipmentApproved) return undefined;
    const keyword = searchKeyword.trim();
    if (!keyword) return undefined;

    const timer = setTimeout(() => {
      if (selectVoucherByScan(keyword)) return;

      const voucherKey = keyword.toLowerCase();
      const stillTyping = allDeliveryNoteDetails.some((detail) =>
        (detail.Order?.VoucherNo || '').toLowerCase().startsWith(voucherKey)
      );
      if (!stillTyping) {
        setError(`Voucher not found: ${keyword}`);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [searchKeyword, selectVoucherByScan, isShipmentApproved, allDeliveryNoteDetails]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load company profile
  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const response = await MainService.GetCompanyProfile();
        setCompanyProfile(response);
      } catch (err) {
        console.error('Error loading company profile:', err);
      }
    };
    loadCompanyProfile();
  }, []);

  // Load emirates for address change
  useEffect(() => {
    const loadEmirates = async () => {
      try {
        const response = await MainService.EmiratesInDropDown();
        if (response && Array.isArray(response)) {
          setEmirates(response);
        }
      } catch (err) {
        console.error('Error loading emirates:', err);
      }
    };
    loadEmirates();
  }, []);

  // Load reasons for dropdown
  useEffect(() => {
    const loadReasons = async () => {
      try {
        const response = await MainService.ReasonsInGrid({ Page: 1, PageSize: 100 });
        if (response && response.Data) {
          setReasons(response.Data);
        } else if (Array.isArray(response)) {
          setReasons(response);
        }
      } catch (err) {
        console.error('Error loading reasons:', err);
      }
    };
    loadReasons();
  }, []);

  // Load statuses for dropdown
  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const response = await MainService.StatusInShipping();
        if (response && Array.isArray(response)) {
          setStatuses(response);
        } else if (response && response.Data) {
          setStatuses(response.Data);
        }
      } catch (err) {
        console.error('Error loading statuses:', err);
      }
    };
    loadStatuses();
  }, []);

  // Load StatusInDropDown for summary
  useEffect(() => {
    const loadStatusInDropDown = async () => {
      try {
        const response = await MainService.StatusInDropDown();
        if (response && Array.isArray(response)) {
          setStatusInDropDown(response);
        } else if (response && response.Data) {
          setStatusInDropDown(response.Data);
        }
      } catch (err) {
        console.error('Error loading status dropdown:', err);
      }
    };
    loadStatusInDropDown();
  }, []);

  // Load expenses for dropdown
  useEffect(() => {
    const loadExpenses = async () => {
      try {
        const response = await MainService.DriversExpensesInDropDown();
        if (response && Array.isArray(response)) {
          setExpenses(response);
        } else if (response && response.Data) {
          setExpenses(response.Data);
        }
      } catch (err) {
        console.error('Error loading expenses:', err);
      }
    };
    loadExpenses();
  }, []);

  // Load driver expenses
  const loadDriverExpenses = async (driverId, selectedShipmentExpenses = []) => {
    if (!driverId) return;
    try {
      const response = await MainService.getDriverExpenseInShipping(driverId);
      const baseExpenses = parseApiList(response).map(exp => ({ ...exp, IsSelected: false }));
      setDriverExpenses(mergeSelectedShipmentExpenses(baseExpenses, selectedShipmentExpenses));
    } catch (err) {
      console.error('Error loading driver expenses:', err);
    }
  };

  // Load next shipment number
  const loadNextShipmentNo = async () => {
    try {
      const response = await MainService.GetNextShipmentNo();
      if (response) {
        setNextShipmentNo(response);
      }
    } catch (err) {
      console.error('Error loading next shipment number:', err);
    }
  };

  // Add/Update driver expense
  const handleAddDriverExpense = async () => {
    if (!deliveryNote?.DriverId) {
      setError('Driver not found');
      return;
    }
    if (!currentExpense.ExpenseId) {
      setError('Select Expense');
      return;
    }
    if (!currentExpense.Amount || currentExpense.Amount <= 0) {
      setError('Enter Amount');
      return;
    }

    try {
      setLoading(true);
      const expenseData = {
        ...currentExpense,
        EmployeeId: deliveryNote.DriverId
      };
      
      // If editing, include the expense ID
      if (selectedExpense?.Id) {
        expenseData.Id = selectedExpense.Id;
      }
      
      const response = await MainService.saveDriverExpense(expenseData);
      if (response === true || response === 'true' || response === 'Success') {
        setSuccess(selectedExpense ? 'Expense updated successfully' : 'Expense added successfully');
        setCurrentExpense({ ExpenseId: '', Amount: 0, BillNo: '' });
        setSelectedExpense(null);
        await loadDriverExpenses(deliveryNote.DriverId, linkedShipmentExpenses);
      } else {
        setError('Failed to save expense');
      }
    } catch (err) {
      console.error('Error saving expense:', err);
      setError('Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  // Toggle expense selection
  const handleToggleExpense = (expenseId) => {
    setDriverExpenses((prev) => {
      const next = prev.map((exp) =>
        exp.Id === expenseId ? { ...exp, IsSelected: !exp.IsSelected } : exp
      );
      setLinkedShipmentExpenses(
        sanitizeShipmentExpensesPayload(
          next
            .filter((e) => e.IsSelected)
            .map((e) => ({
              Amount: Number(e.Amount) || 0,
              DriverExpenseId: e.Id,
              ExpenseId: e.ExpenseId,
            }))
        )
      );
      return next;
    });
  };

  // Edit driver expense
  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setCurrentExpense({
      ExpenseId: expense.ExpenseId || '',
      Amount: expense.Amount || 0,
      BillNo: expense.BillNo || ''
    });
  };

  // View expense bill
  const handleViewExpenseBill = (expense) => {
    setSelectedExpense(expense);
    setShowExpenseBillModal(true);
  };

  // Show delete confirmation dialog
  const handleDeleteExpenseClick = (expense) => {
    setExpenseToDelete(expense);
    setShowDeleteExpenseDialog(true);
  };

  // Delete driver expense (after confirmation)
  const handleDeleteExpense = async () => {
    if (!expenseToDelete?.Id || !deliveryNote?.DriverId) return;
    
    try {
      setLoading(true);
      const response = await MainService.deleteDriverExpense(expenseToDelete.Id);
      if (response === true || response === 'true' || response === 'Success') {
        setSuccess('Expense deleted successfully');
        setShowDeleteExpenseDialog(false);
        setExpenseToDelete(null);
        await loadDriverExpenses(deliveryNote.DriverId, linkedShipmentExpenses);
      } else {
        setError('Failed to delete expense');
      }
    } catch (err) {
      console.error('Error deleting expense:', err);
      setError('Failed to delete expense');
    } finally {
      setLoading(false);
    }
  };

  // Update shipment totals when expenses change
  useEffect(() => {
    const selected = driverExpenses.filter((e) => e.IsSelected);
    const totalExpense =
      selected.length > 0
        ? selected.reduce((sum, e) => sum + (e.Amount || 0), 0)
        : linkedShipmentExpenses.reduce(
            (sum, e) => sum + (normalizeShipmentExpenseLink(e).Amount || 0),
            0
          );
    setShipment((prev) => ({
      ...prev,
      TotalExpense: totalExpense,
      CashInHand: prev.ExpectedAmount - totalExpense,
      BalanceIncome: prev.TotalIncome - totalExpense,
    }));
  }, [driverExpenses, linkedShipmentExpenses, shipment.ExpectedAmount, shipment.TotalIncome]);

  // Handle save shipment
  const handleSaveShipment = async (shouldPrint = false) => {
    const activeDeliveryNoteId = deliveryNoteId || shipment.DeliveryNoteId || deliveryNote?.Id;
    if (!activeDeliveryNoteId) {
      setError('Delivery note not found');
      return;
    }
    
    try {
      setLoading(true);
      const shipmentExpenses = buildShipmentExpensesPayload();
      const totalExpense = shipmentExpenses.reduce((sum, e) => sum + (e.Amount || 0), 0);

      const currentDate = new Date(shipment.Date);
      const shipmentData = {
        ...shipment,
        ...(isEditMode ? { Id: shipment.Id ?? shipment?.id ?? shipmentId } : {}),
        TotalExpense: totalExpense,
        CashInHand: shipment.ExpectedAmount - totalExpense,
        BalanceIncome: shipment.TotalIncome - totalExpense,
        RefNo: isEditMode ? shipment.RefNo : (nextShipmentNo || shipment.RefNo),
        DeliveryNoteId: activeDeliveryNoteId,
        ShipmentExpenses: shipmentExpenses,
        Month: currentDate.getMonth() + 1,
        Day: currentDate.getDate(),
        Year: currentDate.getFullYear()
      };

      const response = await MainService.SaveShipment(shipmentData);
      if (isApiSuccess(response)) {
        setLinkedShipmentExpenses(shipmentExpenses);
        setSuccess('Shipment saved successfully');
        setShowSaveShipmentModal(false);
        const savedShipmentId = response.ShipmentId || response.shipmentId || shipment.Id;
        if (shouldPrint && savedShipmentId) {
          await handlePrintShipment(savedShipmentId);
        }
        if (isEditMode) {
          await loadShipmentForEdit();
        } else {
          window.dispatchEvent(
            new CustomEvent('ainsoft:close-tab', {
              detail: {
                path: location.pathname,
                nextPath: '/home/shipments/new-shipment',
              },
            })
          );
        }
      } else {
        setError(getApiErrorMessage(response));
      }
    } catch (err) {
      console.error('Error saving shipment:', err);
      setError(getApiErrorMessage(err) || 'Failed to save shipment');
    } finally {
      setLoading(false);
    }
  };

  // Handle print shipment
  const handlePrintShipment = async (shipmentId) => {
    if (!shipmentId) return;
    try {
      setLoading(true);
      const response = await MainService.PrintShipment(shipmentId);
      if (response) {
        window.open(`${getApiBaseUrl()}/ReportFiles/${response}`, '_blank');
      }
    } catch (err) {
      console.error('Error printing shipment:', err);
      setError('Failed to print shipment');
    } finally {
      setLoading(false);
    }
  };

  // Load locations when emirate is selected
  const loadLocations = async (emirateId) => {
    if (!emirateId) {
      setLocations([]);
      setFilteredLocations([]);
      return;
    }
    try {
      console.log('Loading locations for EmirateId:', emirateId);
      // GetLocationsByEid expects EmirateId as a query parameter or path parameter
      // Since apiRequest for GET appends data to URL, we need to pass it correctly
      const response = await MainService.GetLocationsByEid(emirateId);
      console.log('GetLocationsByEid response:', response);
      
      // Handle different response structures
      let locationsList = [];
      if (response) {
        if (Array.isArray(response)) {
          locationsList = response;
        } else if (response.Data && Array.isArray(response.Data)) {
          locationsList = response.Data;
        } else if (response.data && Array.isArray(response.data)) {
          locationsList = response.data;
        }
      }
      
      setLocations(locationsList);
      setFilteredLocations(locationsList);
    } catch (err) {
      console.error('Error loading locations:', err);
      setLocations([]);
      setFilteredLocations([]);
    }
  };

  // Filter locations based on search term
  useEffect(() => {
    if (!locationSearchTerm) {
      setFilteredLocations(locations);
    } else {
      const filtered = locations.filter(location =>
        location.Name?.toLowerCase().includes(locationSearchTerm.toLowerCase())
      );
      setFilteredLocations(filtered);
    }
  }, [locationSearchTerm, locations]);

  // Close location dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setIsLocationDropdownOpen(false);
        setLocationSearchTerm('');
      }
    };
    if (isLocationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isLocationDropdownOpen]);

  const selectShipmentLocation = (location) => {
    setSelectedAddress((prev) => ({
      ...prev,
      LocationId: location.Id,
      Location: { Name: location.Name },
    }));
    setIsLocationDropdownOpen(false);
    setLocationSearchTerm('');
  };

  const locationListOpen = isLocationDropdownOpen && Boolean(selectedAddress.EmirateId) && filteredLocations.length > 0;
  const locationNav = useListKeyboardNav({
    isOpen: locationListOpen,
    itemCount: filteredLocations.length,
    onSelectIndex: (index) => filteredLocations[index] && selectShipmentLocation(filteredLocations[index]),
    onClose: () => {
      setIsLocationDropdownOpen(false);
      setLocationSearchTerm('');
    },
    resetKey: locationSearchTerm,
  });

  // Handle right-click context menu
  const handleContextMenu = (e, detail) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      order: detail
    });
  };

  // Close context menu
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu({ visible: false, x: 0, y: 0, order: null });
    };
    if (contextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.visible]);

  // Handle context menu actions
  const handleDriverRemarks = () => {
    const order = contextMenu.order;
    setSelectedOrder(order);
    setDriverRemarksData({
      DriverRemarks: order.Order?.DriverRemarks || '',
      ReasonId: order.Order?.ReasonId || '',
      Remarks: order.Order?.Remarks || ''
    });
    setShowDriverRemarksModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, order: null });
  };

  // Save driver remarks and reason
  const handleSaveDriverRemarks = async () => {
    if (!selectedOrder?.Order?.Id) return;
    
    try {
      setLoading(true);
      const orderData = {
        Id: selectedOrder.Order.Id,
        DriverRemarks: driverRemarksData.DriverRemarks,
        ReasonId: driverRemarksData.ReasonId ? parseInt(driverRemarksData.ReasonId) : null,
        Remarks: driverRemarksData.Remarks
      };
      
      const response = await MainService.ChangeOrderDriverRemarks(orderData);
      if (response === true || response === 'true' || response === 'Success') {
        setSuccess('Driver remarks and reason updated successfully');
        setShowDriverRemarksModal(false);
        setSelectedOrder(null);
        setDriverRemarksData({ DriverRemarks: '', ReasonId: '', Remarks: '' });
        await loadDeliveryNoteDetails();
      } else {
        setError('Failed to update driver remarks');
      }
    } catch (err) {
      console.error('Error updating driver remarks:', err);
      setError('Failed to update driver remarks');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeAddress = async () => {
    if (contextMenu.order?.Order?.CustomerAddress) {
      const address = { ...contextMenu.order.Order.CustomerAddress };
      setSelectedAddress(address);
      setSelectedOrder(contextMenu.order);
      setShowChangeAddressModal(true);
      setContextMenu({ visible: false, x: 0, y: 0, order: null });
      
      // Load locations if emirate is already selected
      if (address.EmirateId) {
        await loadLocations(address.EmirateId);
      }
    }
  };

  const handleViewProof = () => {
    setSelectedOrder(contextMenu.order);
    setShowProofModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, order: null });
  };

  const closeEditPaymentsModal = () => {
    setShowEditPaymentsModal(false);
    setEditPaymentOrder(null);
    setEditPaymentsLoading(false);
  };

  const recalculateEditPaymentOrder = (patch) => {
    setEditPaymentOrder((prev) => {
      if (!prev || !companyProfile) return prev;
      const merged = patch ? { ...prev, ...patch } : prev;
      const productRaw = merged.ProductPrice;
      const deliveryRaw = merged.DeliveryCharge;
      // Keep interim typing values until a real number is entered
      if (
        productRaw === '' ||
        productRaw === '-' ||
        productRaw === '.' ||
        productRaw === '-.' ||
        deliveryRaw === '' ||
        deliveryRaw === '-' ||
        deliveryRaw === '.' ||
        deliveryRaw === '-.'
      ) {
        return merged;
      }
      return calculateOrderPaymentTotals(
        {
          ...merged,
          ProductPrice: toFiniteNumber(merged.ProductPrice),
          DeliveryCharge: toFiniteNumber(merged.DeliveryCharge),
          TempServiceCharge: toFiniteNumber(merged.TempServiceCharge ?? merged.DeliveryCharge),
        },
        companyProfile
      );
    });
  };

  const handleEditPayments = async () => {
    const orderId = contextMenu.order?.Order?.Id ?? contextMenu.order?.OrderId;
    if (!orderId) return;

    setContextMenu({ visible: false, x: 0, y: 0, order: null });
    setEditPaymentsLoading(true);
    setShowEditPaymentsModal(true);
    setEditPaymentOrder(null);
    setError('');

    try {
      const response = await MainService.OrderById(orderId);
      const orderData = normalizeOrderFromApi(response);
      if (!orderData?.Id) {
        setError('Failed to load order details');
        closeEditPaymentsModal();
        return;
      }
      const calculated = companyProfile
        ? calculateOrderPaymentTotals(orderData, companyProfile)
        : orderData;
      setEditPaymentOrder(calculated);
    } catch (err) {
      console.error('Error loading order for payment edit:', err);
      setError('Failed to load order details');
      closeEditPaymentsModal();
    } finally {
      setEditPaymentsLoading(false);
    }
  };

  const calculateShipmentTotalsFromDetails = (details, totalExpense) => {
    let expectedAmount = 0;
    let totalIncome = 0;
    let totalVat = 0;
    let totalAdvance = 0;
    let totalMoneyFromClient = 0;
    let totalClientMoney = 0;

    details.forEach((detail) => {
      const status = detail.Status || {};
      const order = detail.Order;
      if (!order) return;

      if (status.Keyword === 'DLV' || status.Keyword === 'HDLV') {
        expectedAmount += order.CustomerAmount || 0;
        totalIncome += order.DeliveryCharge || 0;
        totalVat += order.TAX || 0;

        if (order.IsAdvancePay) {
          totalAdvance += (order.DeliveryCharge || 0) + (order.TAX || 0);
        }

        if (order.PayType === 'CAD') {
          totalMoneyFromClient += (order.DeliveryCharge || 0) + (order.TAX || 0);
        }

        totalClientMoney += order.ClientAmount || 0;
      }

      if (status.Keyword === 'CNL' && status.Name !== 'Cancelled') {
        totalIncome += order.DeliveryCharge || 0;
        totalVat += order.TAX || 0;

        if (order.IsAdvancePay) {
          totalAdvance += (order.DeliveryCharge || 0) + (order.TAX || 0);
        }

        if (order.PayType === 'CAD') {
          totalMoneyFromClient += (order.DeliveryCharge || 0) + (order.TAX || 0);
        }

        if (order.PayType === 'COD') {
          expectedAmount += order.CustomerAmount || 0;
        }
      }
    });

    return {
      ExpectedAmount: expectedAmount,
      TotalIncome: totalIncome,
      TotalVat: totalVat,
      TotalAdvance: totalAdvance,
      TotalMoneyFromClient: totalMoneyFromClient,
      TotalClientMoney: totalClientMoney,
      CashInHand: expectedAmount - totalExpense,
      BalanceIncome: totalIncome - totalExpense,
    };
  };

  const autoSaveShipmentAfterStatusChange = async (latestDetails, activeDeliveryNoteId) => {
    const activeShipmentId = shipment?.Id ?? shipment?.id ?? shipmentId;
    if (!isEditMode || !activeShipmentId) return true;

    const shipmentExpenses = buildShipmentExpensesPayload();
    const totalExpense = shipmentExpenses.reduce((sum, e) => sum + (e.Amount || 0), 0);
    const totals = calculateShipmentTotalsFromDetails(latestDetails, totalExpense);
    const currentDate = new Date(shipment.Date);
    const shipmentData = {
      ...shipment,
      Id: activeShipmentId,
      ...totals,
      TotalExpense: totalExpense,
      RefNo: shipment.RefNo || nextShipmentNo,
      DeliveryNoteId: activeDeliveryNoteId,
      ShipmentExpenses: shipmentExpenses,
      Month: currentDate.getMonth() + 1,
      Day: currentDate.getDate(),
      Year: currentDate.getFullYear()
    };

    const response = await MainService.SaveShipment(shipmentData);
    if (!isApiSuccess(response)) {
      console.error('Auto SaveShipment failed:', response);
      setError(getApiErrorMessage(response));
      return false;
    }
    setLinkedShipmentExpenses(shipmentExpenses);
    return true;
  };

  // Update order status for selected orders
  const handleUpdateStatus = async () => {
    if (isShipmentApproved) {
      setError('Shipment is approved. Unapprove shipment before changing order status.');
      return;
    }
    if (!selectedStatusId || selectedOrders.size === 0) return;
    const activeDeliveryNoteId = deliveryNoteId || shipment.DeliveryNoteId || deliveryNote?.Id;
    if (!activeDeliveryNoteId) {
      setError('Delivery note not found');
      return;
    }
    
    try {
      setLoading(true);
      const currentDate = new Date();
      const deliveryNoteDetailsToUpdate = Array.from(selectedOrders).map(detailId => {
        const detail = deliveryNoteDetails.find(d => d.Id === detailId);
        return {
          DeliveryNoteId: activeDeliveryNoteId,
          Id: detail.Id,
          OrderId: detail.OrderId,
          StatusId: parseInt(selectedStatusId),
          Order: detail.Order,
          Month: currentDate.getMonth() + 1,
          Day: currentDate.getDate(),
          Year: currentDate.getFullYear()
        };
      });

      const response = await MainService.UpdateOrderDeliveryStatus(deliveryNoteDetailsToUpdate);
      const isSuccess = !isExplicitApiFailure(response);
      if (isSuccess) {
        setSelectedStatusId('');
        setSelectedOrders(new Set());
        setSelectAll(false);
        if (isEditMode) {
          const detailsResponse = await MainService.GetDeliveryDetailsListById(activeDeliveryNoteId);
          const latestDetails = parseApiList(detailsResponse);
          setAllDeliveryNoteDetails(latestDetails);
          const autoSaved = await autoSaveShipmentAfterStatusChange(latestDetails, activeDeliveryNoteId);
          if (!autoSaved) {
            setError('Status updated, but shipment auto-save failed');
            return;
          }
          setSuccess('Status updated and shipment saved successfully');
          await loadShipmentForEdit();
        } else {
          setSuccess('Status updated successfully');
          await loadDeliveryNoteDetails(activeDeliveryNoteId);
        }
      } else {
        setError(response?.Message || response?.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleUnapproveShipment = async () => {
    if (!shipment?.Id) return;

    try {
      setLoading(true);
      setError('');
      const response = await MainService.UnApproveShipment(shipment.Id);

      if (isApiSuccess(response)) {
        setSuccess('Successfully unapproved shipment');
        setShowUnapproveConfirm(false);
        await loadShipmentForEdit();
      } else {
        setError(getApiErrorMessage(response));
      }
    } catch (err) {
      console.error('Error unapproving shipment:', err);
      setError('Failed to unapprove shipment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShipment = async () => {
    if (!shipment?.Id) return;

    try {
      setLoading(true);
      setError('');
      const response = await MainService.DeleteShipment(shipment.Id);

      if (isApiSuccess(response)) {
        setSuccess('Shipment deleted successfully');
        setShowDeleteShipmentConfirm(false);
        navigate('/home/shipments/shipment-list');
        return;
      }

      setError(getApiErrorMessage(response) || 'Failed to delete shipment');
    } catch (err) {
      console.error('Error deleting shipment:', err);
      setError(getApiErrorMessage(err) || 'Failed to delete shipment');
    } finally {
      setLoading(false);
    }
  };

  // Calculate order status summary
  useEffect(() => {
    if (deliveryNoteDetails.length === 0 || statusInDropDown.length === 0) {
      setOrderStatusSummary([]);
      return;
    }

    const orderList = deliveryNoteDetails;
    const orderStatus = [...statusInDropDown];
    
    // Add INT status if it exists in Status2
    const intStatus = statusInDropDown.find(s => s.Keyword === 'INT');
    if (intStatus && !orderStatus.find(s => s.Id === intStatus.Id)) {
      orderStatus.push(intStatus);
    }

    const summary = orderStatus.map(status => {
      const orders = orderList.filter(detail => detail.Status?.Name === status.Name);
      const orderObjects = orders.map(detail => detail.Order).filter(Boolean);
      
      let productMoney = 0;
      let deliveryCharge = 0;
      let vat = 0;
      let customerMoney = 0;
      let clientMoney = 0;

      orderObjects.forEach(order => {
        productMoney += order.ProductPrice || 0;
        deliveryCharge += order.DeliveryCharge || 0;
        vat += order.TAX || 0;
        customerMoney += order.CustomerAmount || 0;
        clientMoney += order.ClientAmount || 0;
      });

      return {
        ...status,
        Count: orders.length,
        Orders: orderObjects,
        ProductMoney: productMoney,
        DeliveryCharge: deliveryCharge,
        VAT: vat,
        CustomerMoney: customerMoney,
        ClientMoney: clientMoney
      };
    });

    // Calculate shipment totals
    let expectedAmount = 0;
    let totalIncome = 0;
    let totalVat = 0;
    let totalAdvance = 0;
    let totalMoneyFromClient = 0;
    let totalClientMoney = 0;

    summary.forEach(status => {
      status.Orders.forEach(order => {
        if (status.Keyword === 'DLV' || status.Keyword === 'HDLV') {
          expectedAmount += order.CustomerAmount || 0;
          totalIncome += order.DeliveryCharge || 0;
          totalVat += order.TAX || 0;
          
          if (order.IsAdvancePay) {
            totalAdvance += (order.DeliveryCharge || 0) + (order.TAX || 0);
          }
          
          if (order.PayType === 'CAD') {
            totalMoneyFromClient += (order.DeliveryCharge || 0) + (order.TAX || 0);
          }
          
          totalClientMoney += order.ClientAmount || 0;
        }
        
        if (status.Keyword === 'CNL' && status.Name !== 'Cancelled') {
          totalIncome += order.DeliveryCharge || 0;
          totalVat += order.TAX || 0;
          
          if (order.IsAdvancePay) {
            totalAdvance += (order.DeliveryCharge || 0) + (order.TAX || 0);
          }
          
          if (order.PayType === 'CAD') {
            totalMoneyFromClient += (order.DeliveryCharge || 0) + (order.TAX || 0);
          }
          
          if (order.PayType === 'COD') {
            expectedAmount += order.CustomerAmount || 0;
          }
        }
      });
    });

    setShipment(prev => ({
      ...prev,
      ExpectedAmount: expectedAmount,
      TotalIncome: totalIncome,
      TotalVat: totalVat,
      TotalAdvance: totalAdvance,
      TotalMoneyFromClient: totalMoneyFromClient,
      TotalClientMoney: totalClientMoney,
      CashInHand: expectedAmount - prev.TotalExpense,
      BalanceIncome: totalIncome - prev.TotalExpense
    }));

    setOrderStatusSummary(summary.filter(s => s.Count > 0));
  }, [deliveryNoteDetails, statusInDropDown, shipment.TotalExpense]);

  // Save payment changes
  const handleSavePayments = async () => {
    if (!editPaymentOrder?.Id || !companyProfile) return;

    try {
      setLoading(true);
      setError('');
      const calculated = calculateOrderPaymentTotals(editPaymentOrder, companyProfile);
      const payload = buildSaveOrderPayload(calculated);
      const response = await MainService.SaveOrder(payload);

      if (isSaveOrderSuccess(response)) {
        setSuccess('Payment details updated successfully');
        closeEditPaymentsModal();
        await loadDeliveryNoteDetails();
      } else {
        setError(response?.Status || 'Failed to update payment details');
      }
    } catch (err) {
      console.error('Error updating payments:', err);
      setError('Failed to update payment details');
    } finally {
      setLoading(false);
    }
  };

  // Save address change
  const handleSaveAddress = async () => {
    if (!selectedAddress || !selectedOrder || !selectedAddress.Id) return;
    
    try {
      setLoading(true);
      const addressData = {
        Id: selectedAddress.Id,
        EmirateId: selectedAddress.EmirateId ? parseInt(selectedAddress.EmirateId) : null,
        LocationId: selectedAddress.LocationId ? parseInt(selectedAddress.LocationId) : null,
        POBOX: selectedAddress.POBOX || '',
        Address: selectedAddress.Address || '',
        LocationUrl: selectedAddress.LocationUrl || '',
        IsDefault: selectedAddress.IsDefault || false
      };
      
      const response = await MainService.UpdateCustomerAddress(addressData);
      if (response === true || response === 'true' || response === 'Success') {
        setSuccess('Address updated successfully');
        setShowChangeAddressModal(false);
        setSelectedAddress(null);
        setSelectedOrder(null);
        setIsLocationDropdownOpen(false);
        setLocationSearchTerm('');
        await loadDeliveryNoteDetails();
      } else {
        setError('Failed to update address');
      }
    } catch (err) {
      console.error('Error updating address:', err);
      setError('Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  // Load attachments count
  const getAttachmentCount = async (orderId) => {
    try {
      const response = await MainService.OrderAttachments(orderId);
      if (response && Array.isArray(response)) {
        return response.length;
      }
      return 0;
    } catch {
      return 0;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  // Get row background color based on status keyword
  const getRowBackgroundColor = (statusKeyword) => {
    if (!statusKeyword) return '';
    
    switch (statusKeyword) {
      case 'DLV':
      case 'HDLV':
        return 'bg-green-50 hover:bg-green-100';
      case 'CNL':
        return 'bg-red-50 hover:bg-red-100';
      case 'RTN':
        return 'bg-yellow-50 hover:bg-yellow-100';
      case 'HLD':
        return 'bg-gray-50 hover:bg-gray-100';
      default:
        return 'hover:bg-gray-50';
    }
  };

  // Get base background color (without hover) for sticky columns
  const getBaseBackgroundColor = (statusKeyword) => {
    if (!statusKeyword) return 'bg-white';
    
    switch (statusKeyword) {
      case 'DLV':
      case 'HDLV':
        return 'bg-green-50';
      case 'CNL':
        return 'bg-red-50';
      case 'RTN':
        return 'bg-yellow-50';
      case 'HLD':
        return 'bg-gray-50';
      default:
        return 'bg-white';
    }
  };

  const getOrderCellValue = useCallback(
    (detail, field) => getConvertShipmentCellValue(detail, field, formatDate),
    []
  );

  const ordersGrid = useClientListGrid({
    columns: CONVERT_SHIPMENT_COLUMNS,
    rows: deliveryNoteDetails,
    getCellValue: getOrderCellValue,
  });

  const renderOrderCell = useCallback((detail, column) => {
    const order = detail.Order || {};
    switch (column.field) {
      case 'voucher':
        return <GridText value={order.VoucherNo} className="font-medium text-gray-900" />;
      case 'status':
        return <StatusPill name={detail.Status?.Name} />;
      case 'productPrice':
      case 'deliveryCharge':
      case 'tax':
      case 'customerAmount':
      case 'clientAmount': {
        const rawMap = {
          productPrice: order.ProductPrice,
          deliveryCharge: order.DeliveryCharge,
          tax: order.TAX,
          customerAmount: order.CustomerAmount,
          clientAmount: order.ClientAmount,
        };
        const raw = rawMap[column.field];
        return (
          <GridText
            value={raw !== null && raw !== undefined ? `AED ${formatCurrency(raw)}` : '-'}
          />
        );
      }
      case 'pickup':
        return (
          <input
            type="checkbox"
            checked={order.IsPickup || false}
            disabled
            className="w-4 h-4"
          />
        );
      default:
        return <GridText value={getConvertShipmentCellValue(detail, column.field, formatDate)} />;
    }
  }, []);

  const getOrderCellClassName = useCallback((detail, column) => {
    const isSelected = selectedOrders.has(detail.Id);
    const bg = isSelected ? 'bg-[#d4edda]' : getBaseBackgroundColor(detail.Status?.Keyword);
    if (column.field === 'voucher') {
      return `whitespace-nowrap sticky left-0 z-10 border-r border-gray-200 shadow-sm min-w-[10rem] max-w-[12rem] w-[10rem] ${bg}`;
    }
    if (column.field === 'status') {
      return `whitespace-nowrap sticky left-[10rem] z-10 border-r border-gray-200 shadow-sm min-w-[8rem] w-[8rem] ${bg}`;
    }
    return '';
  }, [selectedOrders]);

  const handleSelectAllOrders = useCallback((checked) => {
    if (isShipmentApproved) return;
    setSelectAll(checked);
    if (checked) {
      setSelectedOrders(new Set(ordersGrid.processedRows.map((d) => d.Id)));
    } else {
      setSelectedOrders(new Set());
    }
  }, [isShipmentApproved, ordersGrid.processedRows]);

  const handleToggleOrderSelection = useCallback((detail, checked) => {
    if (isShipmentApproved) return;
    const newSelected = new Set(selectedOrders);
    if (checked) {
      newSelected.add(detail.Id);
    } else {
      newSelected.delete(detail.Id);
    }
    setSelectedOrders(newSelected);
    setSelectAll(newSelected.size === ordersGrid.processedRows.length && ordersGrid.processedRows.length > 0);
  }, [isShipmentApproved, ordersGrid.processedRows, selectedOrders]);

  useEffect(() => {
    const rows = ordersGrid.processedRows;
    setSelectAll(rows.length > 0 && rows.every((d) => selectedOrders.has(d.Id)));
  }, [selectedOrders, ordersGrid.processedRows]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(isEditMode ? '/home/shipments/shipment-list' : '/home/shipments/new-shipment')}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditMode ? 'Edit Shipment' : 'Convert Shipment'} {nextShipmentNo && `- ${nextShipmentNo}`}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {isEditMode ? 'Edit existing shipment details' : 'Convert delivery note to shipment'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => (isEditMode ? loadShipmentForEdit() : loadDeliveryNoteDetails())}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {isEditMode && isShipmentApproved ? (
              <button
                onClick={() => setShowUnapproveConfirm(true)}
                disabled={loading}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title="Unapprove shipment"
              >
                <RotateCcw className="w-4 h-4" />
                Unapprove
              </button>
            ) : null}
            {isEditMode && shipment?.Id ? (
              <button
                onClick={() => handlePrintShipment(shipment.Id)}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title="Print shipment"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            ) : null}
            {isEditMode && shipment?.Id ? (
              <button
                onClick={() => setShowDeleteShipmentConfirm(true)}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title="Delete shipment"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            ) : null}
            <button
              onClick={async () => {
                // Check for INT status orders
                const hasIntStatus = deliveryNoteDetails.some(detail => detail.Status?.Keyword === 'INT');
                if (hasIntStatus) {
                  setError('Order status in transit, please update all orders');
                  return;
                }
                
                if (!isEditMode) {
                  await loadNextShipmentNo();
                }
                if (!isEditMode && deliveryNote?.DriverId) {
                  await loadDriverExpenses(deliveryNote.DriverId);
                }
                setShowSaveShipmentModal(true);
              }}
              disabled={loading || isShipmentApproved}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isEditMode ? 'Update' : 'Save Shipment'}
            </button>
            <button
              onClick={async () => {
                // Check for INT status orders
                const hasIntStatus = deliveryNoteDetails.some(detail => detail.Status?.Keyword === 'INT');
                if (hasIntStatus) {
                  setError('Order status in transit, please update all orders');
                  return;
                }
                
                if (!isEditMode) {
                  await loadNextShipmentNo();
                }
                if (!isEditMode && deliveryNote?.DriverId) {
                  await loadDriverExpenses(deliveryNote.DriverId);
                }
                setShowSaveShipmentModal(true);
              }}
              disabled={loading || isShipmentApproved}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Save & Print
            </button>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div className="fixed top-20 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-40 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-20 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-40 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Delivery Note Info */}
      {deliveryNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Note</label>
              <p className="text-sm text-gray-900 font-semibold">{deliveryNote.RefNo || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
              <p className="text-sm text-gray-900">
                {deliveryNote.Vehicle ? (
                  `${deliveryNote.Vehicle.Number || ''}${deliveryNote.Vehicle.Model ? ` (${deliveryNote.Vehicle.Model})` : ''}`
                ) : deliveryNote.VehicleId ? (
                  `Vehicle ID: ${deliveryNote.VehicleId}`
                ) : '-'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
              <p className="text-sm text-gray-900">{deliveryNote.Driver?.Name || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <p className="text-sm text-gray-900">{formatDate(deliveryNote.Date)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <p className="text-sm text-gray-900">{deliveryNote.Status || '-'}</p>
            </div>
            {isEditMode ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shipment</label>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                  isShipmentApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {isShipmentApproved ? 'Approved' : 'Pending'}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Orders ({ordersGrid.totalCount})
            </h2>
            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="Scan voucher no..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                />
              </div>
              <select
                value={selectedStatusId}
                onChange={(e) => setSelectedStatusId(e.target.value)}
                disabled={isShipmentApproved}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                <option value="">Select Status</option>
                {statuses.map(status => (
                  <option key={status.Id} value={status.Id}>{status.Name}</option>
                ))}
              </select>
              <button
                onClick={handleUpdateStatus}
                disabled={isShipmentApproved || !selectedStatusId || selectedOrders.size === 0 || loading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
        <ClientListGrid
          columns={CONVERT_SHIPMENT_COLUMNS}
          grid={ordersGrid}
          renderCell={renderOrderCell}
          getCellClassName={getOrderCellClassName}
          getRowClassName={(detail) => (
            selectedOrders.has(detail.Id)
              ? SELECTED_ROW_CLASS
              : getRowBackgroundColor(detail.Status?.Keyword)
          )}
          onRowContextMenu={handleContextMenu}
          loading={loading}
          loadingText="Loading orders..."
          emptyText="No orders found"
          minWidth="2200px"
          maxHeight={600}
          showPagination
          tableClassName="w-full"
          leadingHeader={
            <th className="w-12 px-4 py-2 text-left">
              <input
                type="checkbox"
                checked={selectAll}
                disabled={isShipmentApproved}
                onChange={(e) => handleSelectAllOrders(e.target.checked)}
                className="h-4 w-4 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </th>
          }
          renderLeadingCell={(detail) => (
            <td className="px-4 py-2 text-center">
              <input
                type="checkbox"
                checked={selectedOrders.has(detail.Id)}
                disabled={isShipmentApproved}
                onChange={(e) => handleToggleOrderSelection(detail, e.target.checked)}
                className="h-4 w-4 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={(e) => e.stopPropagation()}
              />
            </td>
          )}
        />
      </div>

      {/* Order Status Summary */}
      {orderStatusSummary.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status Summary</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Orders</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Product Money</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Delivery Charge</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">VAT</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Customer Money</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">Client Money</th>
                </tr>
              </thead>
              <tbody>
                {orderStatusSummary.map((status, index) => (
                  <tr key={status.Id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 border border-gray-300">{status.Name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">{status.Count}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">AED {formatCurrency(status.ProductMoney)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">AED {formatCurrency(status.DeliveryCharge)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">AED {formatCurrency(status.VAT)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">AED {formatCurrency(status.CustomerMoney)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">AED {formatCurrency(status.ClientMoney)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.visible && contextMenu.order && ReactDOM.createPortal(
          <div
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100000] min-w-[180px]"
            style={{
              top: `${contextMenu.y}px`,
              left: `${contextMenu.x}px`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleDriverRemarks}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
            >
              <FileText className="w-4 h-4" />
              Driver Remarks
            </button>
            <button
              onClick={handleChangeAddress}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
            >
              <MapPin className="w-4 h-4" />
              Change Address
            </button>
            <button
              onClick={handleViewProof}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
            >
              <Eye className="w-4 h-4" />
              Proof
            </button>
            <button
              onClick={handleEditPayments}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left"
            >
              <FileText className="w-4 h-4" />
              Edit Payments
            </button>
          </div>,
          document.body
        )}

        {/* Driver Remarks Modal */}
        {showDriverRemarksModal && selectedOrder && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Driver Remarks</h3>
                <button
                  onClick={() => {
                    setShowDriverRemarksModal(false);
                    setSelectedOrder(null);
                    setDriverRemarksData({ DriverRemarks: '', ReasonId: '', Remarks: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Voucher No</label>
                  <p className="text-sm text-gray-900">{selectedOrder.Order?.VoucherNo || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                  <select
                    value={driverRemarksData.ReasonId || ''}
                    onChange={(e) => setDriverRemarksData(prev => ({ ...prev, ReasonId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select Reason</option>
                    {reasons.map(reason => (
                      <option key={reason.Id} value={reason.Id}>{reason.Description || reason.Name || ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Driver Remarks</label>
                  <textarea
                    value={driverRemarksData.DriverRemarks}
                    onChange={(e) => setDriverRemarksData(prev => ({ ...prev, DriverRemarks: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter driver remarks..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Instructions</label>
                  <textarea
                    value={driverRemarksData.Remarks}
                    onChange={(e) => setDriverRemarksData(prev => ({ ...prev, Remarks: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter delivery instructions..."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDriverRemarksModal(false);
                    setSelectedOrder(null);
                    setDriverRemarksData({ DriverRemarks: '', ReasonId: '', Remarks: '' });
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDriverRemarks}
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Change Address Modal */}
        {showChangeAddressModal && selectedAddress && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Change Address</h3>
                <button
                  onClick={() => {
                    setShowChangeAddressModal(false);
                    setSelectedAddress(null);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emirate</label>
                    <select
                      value={selectedAddress.EmirateId || ''}
                      onChange={(e) => {
                        setSelectedAddress(prev => ({ ...prev, EmirateId: e.target.value }));
                        loadLocations(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select Emirate</option>
                      {emirates.map(emirate => (
                        <option key={emirate.Id} value={emirate.Id}>{emirate.Name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <div className="relative" ref={locationDropdownRef}>
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedAddress.EmirateId) {
                            setIsLocationDropdownOpen(!isLocationDropdownOpen);
                            if (!isLocationDropdownOpen) {
                              setLocationSearchTerm('');
                            }
                          }
                        }}
                        disabled={!selectedAddress.EmirateId}
                        className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white disabled:bg-gray-50 disabled:cursor-not-allowed flex items-center justify-between"
                      >
                        <span className={selectedAddress.LocationId ? 'text-gray-900' : 'text-gray-500'}>
                          {selectedAddress.LocationId 
                            ? locations.find(l => l.Id === selectedAddress.LocationId)?.Name || 'Select Location'
                            : 'Select Location'}
                        </span>
                        <svg className={`w-5 h-5 text-gray-400 transition-transform ${isLocationDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {isLocationDropdownOpen && selectedAddress.EmirateId && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-gray-200">
                            <input
                              type="text"
                              value={locationSearchTerm}
                              onChange={(e) => setLocationSearchTerm(e.target.value)}
                              onKeyDown={locationNav.onKeyDown}
                              placeholder="Search location..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto" role="listbox">
                            {filteredLocations.length > 0 ? (
                              filteredLocations.map((location, index) => (
                                <button
                                  key={location.Id}
                                  type="button"
                                  role="option"
                                  aria-selected={index === locationNav.highlightedIndex}
                                  ref={(node) => locationNav.setItemRef(index, node)}
                                  onMouseEnter={() => locationNav.setHighlightedIndex(index)}
                                  onClick={() => selectShipmentLocation(location)}
                                  className={`w-full px-3 py-2 text-left text-sm ${
                                    index === locationNav.highlightedIndex || selectedAddress.LocationId === location.Id
                                      ? 'bg-blue-50 text-blue-900'
                                      : 'text-gray-900 hover:bg-gray-100'
                                  }`}
                                >
                                  {location.Name}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                No locations found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <textarea
                    value={selectedAddress.Address || ''}
                    onChange={(e) => setSelectedAddress(prev => ({ ...prev, Address: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PO Box</label>
                  <input
                    type="text"
                    value={selectedAddress.POBOX || ''}
                    onChange={(e) => setSelectedAddress(prev => ({ ...prev, POBOX: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowChangeAddressModal(false);
                    setSelectedAddress(null);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAddress}
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Proof Modal */}
        {showProofModal && selectedOrder && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Delivery Proof</h3>
                <button
                  onClick={() => {
                    setShowProofModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Signature</h4>
                  {selectedOrder.Order?.Signature ? (
                    <img
                      src={`${getApiBaseUrl()}${selectedOrder.Order.Signature}`}
                      alt="Signature"
                      className="w-full border border-gray-200 rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-500">No signature available</p>
                  )}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Proof of Delivery</h4>
                  {selectedOrder.Order?.Proof ? (
                    <img
                      src={`${getApiBaseUrl()}${selectedOrder.Order.Proof}`}
                      alt="Proof of Delivery"
                      className="w-full border border-gray-200 rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-500">No proof available</p>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowProofModal(false);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Edit Payments Modal */}
        {showEditPaymentsModal && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Edit Payments</h3>
                <button
                  onClick={closeEditPaymentsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {editPaymentsLoading || !editPaymentOrder ? (
                <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Loading order details…</span>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Voucher No</label>
                        <p className="text-sm text-gray-900">{editPaymentOrder.VoucherNo || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                        <p className="text-sm text-gray-900">{editPaymentOrder.Client?.Name || '-'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Product Price</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editPaymentOrder.ProductPrice}
                          onChange={(e) => {
                            const value = parseSignedNumberInput(e.target.value);
                            if (value === null) return;
                            recalculateEditPaymentOrder({
                              ProductPrice: value,
                            });
                          }}
                          disabled={companyProfile?.IsHidePaymentEditOnOrder && editPaymentOrder.Id > 0}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Charge</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editPaymentOrder.DeliveryCharge}
                          onChange={(e) => {
                            const value = parseSignedNumberInput(e.target.value);
                            if (value === null) return;
                            recalculateEditPaymentOrder({
                              DeliveryCharge: value,
                              TempServiceCharge: value,
                              delchargechanged: true,
                            });
                          }}
                          disabled={companyProfile?.IsHideDeliveryEditOnOrder && editPaymentOrder.Id > 0}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pay Type</label>
                      <select
                        value={editPaymentOrder.PayType || ''}
                        onChange={(e) => recalculateEditPaymentOrder({ PayType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select Pay Type</option>
                        <option value="CAD">CAD</option>
                        <option value="COD">COD</option>
                        <option value="CAO">CAO</option>
                        <option value="TAO">TAO</option>
                        <option value="FOC">FOC</option>
                        <option value="APS">APS</option>
                        <option value="APR">APR</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">TAX</label>
                        <p className="text-sm text-gray-900 font-semibold">AED {formatCurrency(editPaymentOrder.TAX)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer Amount</label>
                        <p className="text-sm text-gray-900 font-semibold">AED {formatCurrency(editPaymentOrder.CustomerAmount)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Client Amount</label>
                        <p className="text-sm text-gray-900 font-semibold">AED {formatCurrency(editPaymentOrder.ClientAmount)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={closeEditPaymentsModal}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePayments}
                      disabled={loading}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Save Shipment Modal */}
        {showSaveShipmentModal && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Update Shipment' : 'Save Shipment'} - {nextShipmentNo || shipment.RefNo || 'New'}
                </h3>
                <button
                  onClick={() => {
                    setShowSaveShipmentModal(false);
                    setCurrentExpense({ ExpenseId: '', Amount: 0, BillNo: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Remarks and Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                    <textarea
                      value={shipment.Remarks}
                      onChange={(e) => setShipment(prev => ({ ...prev, Remarks: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter remarks..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shipment Date</label>
                    <input
                      type="date"
                      value={shipment.Date}
                      onChange={(e) => setShipment(prev => ({ ...prev, Date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Expense Management */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Driver Expenses</h4>
                  
                  {/* Add Expense Form */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Expense</label>
                        <select
                          value={currentExpense.ExpenseId}
                          onChange={(e) => setCurrentExpense(prev => ({ ...prev, ExpenseId: e.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        >
                          <option value="">Select Expense</option>
                          {expenses.map(expense => (
                            <option key={expense.Id} value={expense.Id}>{expense.Name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={currentExpense.Amount}
                          onChange={(e) => setCurrentExpense(prev => ({ ...prev, Amount: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Bill No</label>
                        <input
                          type="text"
                          value={currentExpense.BillNo}
                          onChange={(e) => setCurrentExpense(prev => ({ ...prev, BillNo: e.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={handleAddDriverExpense}
                          disabled={loading || !currentExpense.ExpenseId || !currentExpense.Amount}
                          className="w-full px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Add Expense
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expenses List */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Select</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Expense</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Bill No</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Options</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {driverExpenses.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-4 text-center text-gray-500 text-sm">
                              No expenses added
                            </td>
                          </tr>
                        ) : (
                          driverExpenses.map(expense => (
                            <tr key={expense.Id} className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <input
                                  type="checkbox"
                                  checked={expense.IsSelected || false}
                                  onChange={() => handleToggleExpense(expense.Id)}
                                  className="w-4 h-4"
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{expense.Expense?.Name || '-'}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">AED {formatCurrency(expense.Amount)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{expense.BillNo || '-'}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatDate(expense.Date)}</td>
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleViewExpenseBill(expense)}
                                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="View Bill"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleEditExpense(expense)}
                                    className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteExpenseClick(expense)}
                                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Summary</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Expected Amount</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.ExpectedAmount)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Total Expense</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.TotalExpense)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Cash In Hand</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.CashInHand)}</p>
                    </div>
                    {(!companyProfile?.IsBlockIncomeInShipment) && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Total Income</p>
                        <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.TotalIncome)}</p>
                      </div>
                    )}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Total VAT</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.TotalVat)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Total Advance</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.TotalAdvance)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Balance Income</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.BalanceIncome)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Total Money From Client</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.TotalMoneyFromClient)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Total Client Money</p>
                      <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(shipment.TotalClientMoney)}</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleSaveShipment(false)}
                    disabled={loading}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {isEditMode ? 'Update' : 'Save Shipment'}
                  </button>
                  <button
                    onClick={() => handleSaveShipment(true)}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Save & Print
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Expense Bill Modal */}
        {showExpenseBillModal && selectedExpense && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Expense Bill</h3>
                <button
                  onClick={() => {
                    setShowExpenseBillModal(false);
                    setSelectedExpense(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Bill Photo</p>
                  {selectedExpense.Photo ? (
                    <img
                      src={`${getApiBaseUrl()}${selectedExpense.Photo}`}
                      alt="Expense Bill"
                      className="w-full border border-gray-200 rounded-lg"
                    />
                  ) : (
                    <p className="text-gray-500">No bill photo available</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Expense</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExpense.Expense?.Name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="text-sm font-semibold text-gray-900">AED {formatCurrency(selectedExpense.Amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Bill No</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedExpense.BillNo || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date</p>
                    <p className="text-sm font-semibold text-gray-900">{formatDate(selectedExpense.Date)}</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowExpenseBillModal(false);
                    setSelectedExpense(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Unapprove Shipment Confirmation Dialog */}
        {showUnapproveConfirm && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="flex items-start gap-3 border-b border-gray-100 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <RotateCcw className="h-5 w-5 text-amber-700" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900">Unapprove shipment?</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    This will reopen shipment {shipment.RefNo ? <span className="font-medium">{shipment.RefNo}</span> : null} for editing.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowUnapproveConfirm(false)}
                  className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  disabled={loading}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-700">
                  After unapproving, you can change order statuses and save shipment changes again.
                </p>
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
                <button
                  type="button"
                  onClick={() => setShowUnapproveConfirm(false)}
                  disabled={loading}
                  className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUnapproveShipment}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  {loading ? 'Unapproving...' : 'Confirm Unapprove'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Shipment Confirmation Dialog */}
        {showDeleteShipmentConfirm && shipment?.Id && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
            <div className="mx-4 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
              <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
                <div className="rounded-full bg-red-100 p-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Are you sure you want to delete shipment "{shipment.RefNo || nextShipmentNo || '-'}"?
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteShipmentConfirm(false)}
                  className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  disabled={loading}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-3 p-5">
                <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                  <p><span className="font-medium">Ref No:</span> {shipment.RefNo || nextShipmentNo || '-'}</p>
                  <p><span className="font-medium">Delivery Note:</span> {deliveryNote?.RefNo || shipment.DeliveryNote?.RefNo || '-'}</p>
                  <p><span className="font-medium">Status:</span> {isShipmentApproved ? 'Approved' : 'Pending'}</p>
                </div>
                <p className="text-sm text-gray-600">
                  Only unapproved shipments can be deleted. The linked delivery note will be moved back to confirmed.
                </p>
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteShipmentConfirm(false)}
                  disabled={loading}
                  className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteShipment}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Expense Confirmation Dialog */}
        {showDeleteExpenseDialog && expenseToDelete && ReactDOM.createPortal(
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Confirm Delete</h3>
                <button
                  onClick={() => {
                    setShowDeleteExpenseDialog(false);
                    setExpenseToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete this expense?
                </p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Expense:</span> {expenseToDelete.Expense?.Name || '-'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Amount:</span> AED {formatCurrency(expenseToDelete.Amount)}
                  </p>
                  {expenseToDelete.BillNo && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Bill No:</span> {expenseToDelete.BillNo}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteExpenseDialog(false);
                    setExpenseToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteExpense}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

    </div>
  );
};

export default ConvertShipment;

